import { IssueTaskCategory, IssueTaskDoc, IssueTaskStatus } from '@gooduncles/gu-app-schema';
import { AgGridReact } from 'ag-grid-react';
import { DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import { FC, useMemo, useState } from 'react';
import { ISSUE_TASK_ACTIVE_STATUSES } from 'src/constants/issue.constants';

import { formatDate, getDefaultDates } from 'src/utils/date-util';
import { issueTaskStatusBadgeMap } from 'src/utils/issue/issue-util';

import { useIssueTaskHistoryData } from 'src/hooks/subscription/useIssueHistoryData';

import AgGridSettingsButtons from 'src/atomic-components/molecules/AgGridSettingsButtons/AgGridSettingsButtons';
import InventoryDetailModal from 'src/atomic-components/organisms/Modals/InventoryDetailModal';

import IssueTaskHistoryTable, { ISSUE_TASK_HISTORY_TABLE_ID } from '../IssueHistory/IssueTaskHistoryTable';
import IssueInventoryManagementTable, { ISSUE_INVENTORY_MANAGEMENT_TABLE_ID } from './IssueInventoryManagementTable';
import { InventoryIssueRow } from './types';
import { transformToInventoryIssueRows } from './utils';

const { RangePicker } = DatePicker;

// 기본값: 최근 7일
const defaultDates = getDefaultDates(7, 0);

// Status 필터 옵션 (DELETED 제외)
const statusOptions = ISSUE_TASK_ACTIVE_STATUSES.map((status) => ({
  label: issueTaskStatusBadgeMap[status].text,
  value: status,
}));

// 기본 선택 상태
const defaultStatuses = [IssueTaskStatus.IN_PROGRESS, IssueTaskStatus.COMPLETED];

/**
 * 미래의 날짜를 선택하지 못하도록 하는 함수
 */
const disableFutureDate = (current: dayjs.Dayjs) => {
  return current && current > dayjs().endOf('day');
};

/**
 * 물류 이슈 관리 페이지
 * - 좌측: 회수(PICK_UP) 태스크 - 재고 처리 테이블
 * - 우측: 배송(DELIVERY) 태스크 - 히스토리 테이블
 */
const IssueLogisticsManagement: FC = () => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>(defaultDates);
  const [selectedStatuses, setSelectedStatuses] = useState<IssueTaskStatus[]>(defaultStatuses);
  const [pickUpGridRef, setPickUpGridRef] = useState<AgGridReact<InventoryIssueRow> | null>(null);
  const [deliveryGridRef, setDeliveryGridRef] = useState<AgGridReact<IssueTaskDoc> | null>(null);

  const startDate = useMemo(() => (dateRange[0] ? formatDate(dateRange[0].toDate(), 'yyyy-MM-dd') : ''), [dateRange]);
  const endDate = useMemo(() => (dateRange[1] ? formatDate(dateRange[1].toDate(), 'yyyy-MM-dd') : ''), [dateRange]);

  const { tasks, isLoading } = useIssueTaskHistoryData({
    enabled: !!startDate && !!endDate,
    startDate,
    endDate,
  });

  // 상태 필터 적용 후 카테고리별 분리
  const { pickUpTasks, deliveryTasks } = useMemo(() => {
    const statusFiltered = tasks.filter((t) => selectedStatuses.length === 0 || selectedStatuses.includes(t.status));

    return {
      pickUpTasks: statusFiltered.filter((t) => t.category === IssueTaskCategory.PICK_UP),
      deliveryTasks: statusFiltered.filter((t) => t.category === IssueTaskCategory.DELIVERY),
    };
  }, [tasks, selectedStatuses]);

  // 회수 태스크를 재고 처리용 row 데이터로 변환
  const pickUpRowData = useMemo(() => {
    const tasksWithInventory = pickUpTasks.filter(
      (t) =>
        t.inventoryIssueInfo !== null &&
        (t.status === IssueTaskStatus.COMPLETED || t.status === IssueTaskStatus.IN_PROGRESS)
    );
    return transformToInventoryIssueRows(tasksWithInventory);
  }, [pickUpTasks]);

  const handleDateRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    if (dates) {
      setDateRange(dates);
    }
  };

  return (
    <>
      <div className='height100 flexColumn' style={{ gap: 16 }}>
        {/* toolbar */}
        <div className='flexRow flexAlignCenter flexSpaceBetween'>
          <div className='flexRow flexAlignCenter' style={{ gap: 8 }}>
            <span>조회 기간:</span>
            <RangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              disabledDate={disableFutureDate}
              allowClear={false}
            />
          </div>
          <div className='flexRow flexAlignCenter' style={{ gap: 8 }}>
            <span>상태:</span>
            <Select
              mode='multiple'
              placeholder='상태 선택'
              style={{ minWidth: 200 }}
              value={selectedStatuses}
              onChange={setSelectedStatuses}
              options={statusOptions}
            />
          </div>
        </div>

        {/* 상하 분할 레이아웃 */}
        <div className='flexColumn' style={{ flex: 1, minHeight: 0, gap: 16 }}>
          {/* 상단: 회수 (PICK_UP) */}
          <div className='flexColumn' style={{ flex: 1, minHeight: 0 }}>
            <div className='flexRow flexAlignCenter flexSpaceBetween' style={{ marginBottom: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                회수 <span style={{ color: '#8c8c8c', fontWeight: 400 }}>({pickUpRowData.length}건)</span>
              </div>
              <AgGridSettingsButtons gridRef={pickUpGridRef} tableId={ISSUE_INVENTORY_MANAGEMENT_TABLE_ID} size='small' />
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
              <IssueInventoryManagementTable rowData={pickUpRowData} isLoading={isLoading} onGridRefChange={setPickUpGridRef} />
            </div>
          </div>

          {/* 하단: 배송 (DELIVERY) */}
          <div className='flexColumn' style={{ flex: 1, minHeight: 0 }}>
            <div className='flexRow flexAlignCenter flexSpaceBetween' style={{ marginBottom: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                배송 <span style={{ color: '#8c8c8c', fontWeight: 400 }}>({deliveryTasks.length}건)</span>
              </div>
              <AgGridSettingsButtons gridRef={deliveryGridRef} tableId={ISSUE_TASK_HISTORY_TABLE_ID} size='small' />
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
              <IssueTaskHistoryTable rowData={deliveryTasks} isLoading={isLoading} headerHeight={32} groupHeaderHeight={32} onGridRefChange={setDeliveryGridRef} />
            </div>
          </div>
        </div>
      </div>
      <InventoryDetailModal />
    </>
  );
};

export default IssueLogisticsManagement;
